interface StackFrame {
  filename?: string;
  function?: string;
  lineno?: number;
}

interface LimitableEvent {
  fingerprint?: string[];
  message?: string;
  exception?: {
    values?: {
      type?: string;
      value?: string;
      stacktrace?: { frames?: StackFrame[] };
    }[];
  };
}

const LIMITED_ENVIRONMENTS = new Set(["staging", "development"]);
const EVENTS_PER_ERROR_PER_DAY = 100;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

export function sentryEventLimit(environment: string | undefined) {
  if (!environment || !LIMITED_ENVIRONMENTS.has(environment)) {
    return undefined;
  }

  const counts = new Map<string, { since: number; count: number }>();

  return <E extends LimitableEvent>(event: E): E | null => {
    const key = errorKey(event);
    const now = Date.now();
    const current = counts.get(key);

    if (!current || now - current.since >= DAY_IN_MS) {
      counts.set(key, { since: now, count: 1 });
      return event;
    }

    current.count += 1;
    return current.count <= EVENTS_PER_ERROR_PER_DAY ? event : null;
  };
}

function errorKey(event: LimitableEvent) {
  if (event.fingerprint?.length) {
    return event.fingerprint.join("|");
  }

  const exception = event.exception?.values?.at(-1);
  if (!exception) {
    return event.message ?? "";
  }

  const location = (exception.stacktrace?.frames ?? [])
    .map((frame) => `${frame.filename}:${frame.function}:${frame.lineno}`)
    .join(">");
  return `${exception.type}@${location || exception.value}`;
}
